/**
 * busyFormProps — the focus contract for a form that stays on screen while its
 * request runs (Login, ResetPassword, and any form like them).
 *
 * A browser drops focus to <body> from a focused control that becomes
 * `disabled` (Chromium at its next rendering step). A form that disables its
 * fields and its submit button while the request runs therefore throws a
 * keyboard or screen-reader user off the form when they press Enter, and
 * nothing brings them back when the request fails. So while `busy`, nothing
 * that can be holding focus when a submit starts is natively disabled:
 *
 *   - A field is readOnly + aria-disabled. It keeps focus, is announced as
 *     unavailable, and takes typing again when the request settles.
 *   - The submit button is aria-disabled.
 *   - With nothing disabled, the guard here is what refuses a second request:
 *     a read-only field still submits on Enter, and an aria-disabled <button>
 *     still clicks. The submit event is cancelled FIRST, every time, so a
 *     refused submit can never fall through to a native form submission.
 *   - The form is method="post". A submit that never reaches the handler at
 *     all (form.submit(), or a listener that stops the event first) then
 *     carries the fields in a request body, not in the query string of a GET
 *     that reloads the page: a password included, for a login form. The
 *     static host answers that POST with an error page, which is the
 *     intended price.
 *   - A held Enter never submits. With focus kept, an auto-repeating Enter
 *     would resend the moment each answer came back, which a natively
 *     disabled field used to absorb. This cancels the browser's own Enter
 *     (the implicit submit from a field, the click on a button) and leaves a
 *     <textarea> alone, where Enter is a newline. A field with its own Enter
 *     handler (the walkthrough answer box, where Enter submits from a
 *     textarea) has to refuse `e.repeat` itself: its handler runs before
 *     this one.
 *
 * Spread the props onto the form's own markup:
 *
 *   const form = busyFormProps({ busy: saving, onSubmit: save });
 *   <form {...form.formProps}>
 *     <input value={name} onChange={...} {...form.fieldProps} />
 *     <button {...form.submitProps}>Save</button>
 *   </form>
 *
 * What the caller owes it:
 *
 *   - Don't also give those elements onSubmit, onKeyDown, method, action,
 *     readOnly, aria-disabled or type, and above all not `disabled`, by any
 *     route: that is the bug again.
 *   - `busy` has to turn true inside onSubmit before its first `await`. The
 *     guard reads the `busy` of the render the event arrived in, so the state
 *     change has to happen during one press for the next press to see it.
 *     onSubmit receives the (already cancelled) event.
 *   - The request needs a deadline: axios's `timeout`, HELD_REQUEST_TIMEOUT_MS
 *     (config.js). Busy ends only when the request settles, so one that never
 *     does leaves the form taking no typing and refusing every submit until a
 *     reload.
 *   - Style the unavailable look off [aria-disabled="true"], never :disabled,
 *     and not with opacity: it fades the focus ring too.
 *   - If success takes the form away, land focus yourself in a layout effect:
 *     the control that kept focus goes with it.
 *
 * fieldProps is for text-like inputs (text, email, password, number, date…)
 * and textareas. readOnly does nothing on a checkbox, radio, select, file,
 * range or color input, so those need their own treatment before a form that
 * has one adopts this. Don't assume they are safe to leave natively disabled
 * either. Measured in Chromium 152 with real key presses: Enter on a checkbox
 * or a radio submits the form just as it does in a text field, so either can
 * be the control holding focus when the request starts (a closed select did
 * not submit there; the rest, and other browsers, are unmeasured). Nor is a
 * type="button" button safe to disable: a press of the submit button that
 * moves no focus (a screen reader's or voice control's activation, a scripted
 * click(), reportedly a tap in Safari on an iPad or iPhone) leaves focus
 * wherever it was, such a button included.
 * Refuse those with busyButton (components/ui/busyForm.js) instead, as
 * Login's view changers and role toggles do.
 *
 * A chat composer (Enter sends, the box clears) has its own contract:
 * hooks/useChatComposer.
 */
function busyFormProps({ busy, onSubmit }) {
  // `busy` may be any truthy value (an id being saved, say). aria-disabled
  // must still read exactly "true", or nothing announces or styles it.
  const isBusy = Boolean(busy);
  return {
    formProps: {
      method: 'post',
      onSubmit: (e) => {
        e.preventDefault();
        if (!isBusy) onSubmit(e);
      },
      onKeyDown: (e) => {
        if (e.key === 'Enter' && e.repeat && e.target.tagName !== 'TEXTAREA') e.preventDefault();
      },
    },
    fieldProps: {
      readOnly: isBusy,
      'aria-disabled': isBusy || undefined,
    },
    submitProps: {
      type: 'submit',
      'aria-disabled': isBusy || undefined,
    },
  };
}

export default busyFormProps;
